// inclusionContent.js
// ─────────────────────────────────────────────────────────────────────────────
// All editable copy + arrays for the /apply-for-inclusion page live here.
//
// TRUTHFULNESS POLICY (set by the brief):
//   • Don't fabricate live testimonials.
//   • Don't present placeholder reach data as verified.
//   • Use real featured listings where available.
//   • Use real published Edit content where available.
//   • If live data is not available, mark placeholders clearly *in this file*
//    , sections that receive only placeholder data should NOT render publicly
//     unless the placeholder is generic enough to be honest.
//
// This file is the editor's surface. Touch this, not the JSX.
// ─────────────────────────────────────────────────────────────────────────────

// ── PALETTES ────────────────────────────────────────────────────────────────
// Light + dark themes for the page. Hero + Edit Showcase deliberately use the
// DARK palette regardless of the toggle (they are intentional dark moments,
// like magazine spreads inside the brochure). Everything else swaps.
export const PALETTE = {
  light: {
    pageBg:        '#faf7f1',                    // page wrapper
    sectionBg:     '#faf7f1',                    // primary sections
    altSectionBg:  '#f3eee5',                    // alt section (testimonials)
    surface:       '#ffffff',                    // cards
    altSurface:    '#fffbf3',                    // form
    text:          '#16140f',
    body:          'rgba(22,20,15,0.62)',
    muted:         'rgba(22,20,15,0.55)',
    subtle:        'rgba(22,20,15,0.42)',
    border:        'rgba(22,20,15,0.10)',
    border2:       'rgba(22,20,15,0.06)',
  },
  dark: {
    pageBg:        '#070604',
    sectionBg:     '#0c0a07',
    altSectionBg:  '#0e0c09',
    surface:       '#111009',
    altSurface:    '#0e0c09',
    text:          '#f0ebe0',
    body:          'rgba(240,235,224,0.72)',
    muted:         'rgba(240,235,224,0.58)',
    subtle:        'rgba(240,235,224,0.42)',
    border:        'rgba(240,235,224,0.10)',
    border2:       'rgba(240,235,224,0.06)',
  },
};
// Helper: pass darkMode → returns the right palette
export const getPalette = (darkMode) => darkMode ? PALETTE.dark : PALETTE.light;

// ── HERO ────────────────────────────────────────────────────────────────────
export const HERO = {
  eyebrow:  'Membership · By Invitation',
  title:    'Apply for Inclusion',
  // Statement of standard, not an explanation. Two short sentences. First
  // names the collection, second names the posture. Tightened from the
  // earlier "reflects a recognised standard of excellence" because the
  // shorter form ("Inclusion is a recognised standard.") reads as
  // institutional fact rather than editorial copy. First impression in
  // under three seconds.
  subtitle: 'The world’s most exceptional wedding venues, vendors and planners. Inclusion is a recognised standard, not a listing.',
  cta:      'Apply for Inclusion',
  // User-selected hero photograph (Unsplash bQRDNrl6Y4Y), woman in
  // an elaborate white gown on a grand palace staircase, dramatic
  // chiaroscuro. Cinematic, editorial, sets the standard immediately.
  // To swap, replace this URL or save a custom file at
  // /public/apply-for-inclusion/hero.jpg and point this back to it.
  imageUrl: 'https://images.unsplash.com/photo-1768586471676-6af1d219e99e?auto=format&fit=crop&w=2400&q=85',
  imageAlt: 'A bride in an elaborate white gown on the grand staircase of a historic palace',
  // Editorial photo credit, magazine plate style, just the name in tiny
  // caps. Context (corner of a hero) makes the role obvious; no need for
  // "Photographed by" framing.
  photoCredit: 'Dejan Nikolic',
};

// ── THE PROMISE (manifesto) ─────────────────────────────────────────────────
// Four pillars. Short prose, no bullet points on the page.
export const PROMISE_PILLARS = [
  {
    label:  'Discovery',
    body:   'Aura, our AI concierge, surfaces your work to couples whose brief actually matches it. Not search. Not algorithms tuned to volume. Considered, intelligent matching.',
  },
  {
    label:  'Story',
    body:   'Editorial features in The LWD Edit, our publication. Your work is not just listed; it is positioned.',
  },
  {
    label:  'Status',
    body:   'A curated mark. Membership signals that you have been selected, not that you bought a slot.',
  },
  {
    label:  'Access',
    body:   'Direct enquiries from international couples ready to commission. No leadgen middlemen.',
  },
];

// ── INSIDE THE COLLECTION COPY ─────────────────────────────────────────────
// The opening "standard" beat. Geography + prestige framing so the section
// reads as private members board, not placeholders.
export const COLLECTION_COPY = {
  eyebrow:  'Inside the Collection',
  title:    'The standard you’re applying to join',
  // Trimmed ~30%. Less explanation, more implication, the cards do the
  // talking. The destination list is suggestive enough; we don't need to
  // catalogue four regions to land the point.
  body:     'From Lake Como to the French Riviera, from the Amalfi Coast to the Highlands of Scotland.',
  // Below-the-grid footnote, explains what the listing card actually
  // does for couples. Tells the vendor: your listing is a full editorial
  // product, not a static thumbnail. Each body line is kept short enough
  // to render on one line (whiteSpace:nowrap is applied in the JSX).
  footnoteIntro: 'Each listing is a working editorial product, not a static thumbnail. From the card alone, couples can:',
  footnoteFeatures: [
    { label: 'Swipe',    body: 'Full galleries, ambient video.' },
    { label: 'Watch',    body: 'Embedded venue films and Reels.' },
    { label: 'Preview',  body: 'Instant Quick View, no exit.' },
    { label: 'Enquire',  body: 'Direct contact, no middlemen.' },
  ],
  // Showcase explainer, sits between the features grid and the closing
  // CTA stamp. Frames Showcase as a real product (a bespoke editorial
  // micro-site within LWD, not just a directory tile) and routes to a
  // live example so the vendor experiences the level instead of imagining it.
  showcase: {
    eyebrow:  'Beyond Listing',
    title:    'The Showcase Tier',
    // Kicker, sits directly beneath the title in italic serif. Reads
    // like a magazine standfirst beneath a headline.
    kicker:   'Your own editorial home on LWD',
    body:     'For our most considered properties, a Showcase is more than a profile. It is a fully editorial micro-site within LWD: your venue’s own page, not a tile. Hero film, signature story, full gallery, direct routing. Reserved for a small number of properties per destination.',
    exampleLabel: 'See a Showcase',
    // Live Showcase example shipped in the repo. Swap to another existing
    // showcase URL if you’d rather feature a different property.
    exampleHref:  '/showcase/domaine-des-etangs',
  },
};

// ── AUTHORITY STATEMENT (mid-page positional beat) ────────────────────────
// The line that answers the unspoken vendor question:
// "Will I be one of many, or one of few?"
// Sits between Publications and Voices, a quiet, weighted statement that
// frames scarcity right before the page closes the funnel into the form.
export const AUTHORITY_STATEMENT = {
  text: 'We feature a limited number of venues across each destination, ensuring clarity, visibility, and distinction.',
  attribution: 'The Editorial Team',
};

// ── INSIDE THE COLLECTION (live featured listings) ─────────────────────────
// Pulled from the `listings` table at runtime, filtered by featured/published.
//
// SAMPLE_LISTINGS below uses real LWD venue assets already in /public so the
// section always renders 3 cards while the listings table is being populated /
// `is_featured` flagged. Each entry is a real venue with assets shipped in
// the repo. Replace this array (or set listings.is_featured=true on real
// rows in the DB) once the live query returns data.
export const SAMPLE_LISTINGS = [
  {
    id:               'sample-domaine-des-etangs',
    slug:             'domaine-des-etangs',
    name:             'Domaine des Étangs',
    city:             'Massignac',
    region:           'Charente',
    country_slug:     'france',
    cover_image_url:  '/Domaine-des-Etangs-Auberge-Collection/DDE_Castle_Exteriors_2023_33.jpg',
    hero_image_url:   '/Domaine-des-Etangs-Auberge-Collection/DDE_Castle_Exteriors_2023_33.jpg',
    gallery_images: [
      '/Domaine-des-Etangs-Auberge-Collection/Aerial view.jpg',
      '/Domaine-des-Etangs-Auberge-Collection/Castle gardens.jpg',
      '/Domaine-des-Etangs-Auberge-Collection/Wedding long table.jpg',
    ],
    listing_type:     'Venue',
    styles:           ['Château'],
    style:            'Château',
    rating:           4.9,
    reviews:          74,
    price_from:       18000,
    capacity_max:     150,
    description:      'A 14th-century château set in 2,500 acres of private park, lakes and forest in southwest France. The Auberge Collection’s flagship.',
    status:           'published',
    is_featured:      true,
  },
  {
    id:               'sample-grand-tirolia',
    slug:             'grand-tirolia-kitzbuehel',
    name:             'Grand Tirolia, Kitzbühel',
    city:             'Kitzbühel',
    region:           'Tyrol',
    country_slug:     'austria',
    cover_image_url:  '/grand-tirolia/20250820_GTK_DJI_0382-HDR.jpg',
    hero_image_url:   '/grand-tirolia/20250820_GTK_DJI_0382-HDR.jpg',
    gallery_images: [
      '/grand-tirolia/20250819_GTK_DJI_0314.jpg',
      '/grand-tirolia/20250819_GTK_A9_01073.jpg',
    ],
    listing_type:     'Venue',
    styles:           ['Alpine Resort'],
    style:            'Alpine Resort',
    rating:           4.8,
    reviews:          52,
    price_from:       12500,
    capacity_max:     180,
    description:      'A five-star alpine resort overlooking Kitzbühel, with private chapel, panoramic ballroom and Tyrolean dining for destination weddings.',
    status:           'published',
    is_featured:      true,
  },
  {
    id:               'sample-park-lane',
    slug:             'intercontinental-london-park-lane',
    name:             'InterContinental London Park Lane',
    city:             'London',
    region:           'Mayfair',
    country_slug:     'united-kingdom',
    cover_image_url:  '/parklane.intercontinental/sitemgr_photo_23362.jpg',
    hero_image_url:   '/parklane.intercontinental/sitemgr_photo_23362.jpg',
    gallery_images: [
      '/parklane.intercontinental/sitemgr_photo_23363.jpg',
    ],
    listing_type:     'Venue',
    styles:           ['City Hotel'],
    style:            'City Hotel',
    rating:           4.7,
    reviews:          118,
    price_from:       22000,
    capacity_max:     400,
    description:      'A landmark Mayfair address overlooking Hyde Park. The Ballroom hosts up to 400 for receptions; the Wellington Suite for intimate gatherings.',
    status:           'published',
    is_featured:      true,
  },
];

// Generic "selection in progress" tiles, only used when SAMPLE_LISTINGS
// is also empty (rare; failsafe).
export const MEMBERS_FALLBACK = [
  { _isPlaceholder: true, label: 'Italy',   note: 'Curated venue selection in progress' },
  { _isPlaceholder: true, label: 'France',  note: 'Curated venue selection in progress' },
  { _isPlaceholder: true, label: 'Spain',   note: 'Curated venue selection in progress' },
];

// ── AURA SHOWCASE (the discovery engine, LWD's unfair advantage) ──────────
// Aura is the AI concierge that turns LWD from a directory into intelligent
// matching. Every other wedding platform is a search box; LWD reads a
// couple's brief and surfaces members whose work actually fits.
//
// This section is dark by design (mirrors The LWD Edit Showcase rhythm) and
// is positioned between "Inside the Collection" and "The LWD Edit Showcase"
// so the funnel reads: standard → discovery → story.
export const AURA_SHOWCASE = {
  eyebrow:   'Discovery · Aura',
  title:     'Surfaced by intelligence, not search',
  // Three sharp statements, short, declarative, almost brutal. Compressed
  // to subject-omitted form so the rhythm is faster and identical across
  // all three lines. Read in two seconds, not five. Value lands before
  // the explanatory body has a chance to work.
  pillars: [
    'Matched, not listed.',
    'Recommended, not browsed.',
    'Discovered at the moment of intent.',
  ],
  // Single supporting sentence. The three pillars above carry the message;
  // the body is now a footnote, not a paragraph. Felt instantly, not read.
  body:      'Members get found by the right couples, not fifty random ones.',
  // Stylised sample exchange, illustrates the mechanism without
  // pretending to be a verbatim transcript. Tagged as "illustrative" in
  // the JSX so the page stays truthful.
  sample: {
    couple: 'A small wedding in Tuscany next September. Around fifty guests, vineyard views, an outdoor ceremony, ideally somewhere that lets us bring our own caterer.',
    aura:   'Three members fit closely: an estate near San Gimignano with vineyard ceremonies and an open-vendor policy, a private villa in Chianti that hosts up to seventy with a stone terrace for dinner, and a working castello on the Val d’Orcia that handles fifty effortlessly. I’d start with the first two.',
  },
  bullets: [
    'Reads the couple’s brief, including budget, region, style, scale, season.',
    'Surfaces the members whose actual work matches.',
    'Routes the enquiry directly. No middlemen, no broadcasting.',
  ],
  cta:       'Meet Aura',
  // Aura is a chat surface site-wide; this CTA dispatches a global event
  // the launcher already listens for. If unavailable it falls back to
  // a hash anchor.
  ctaEvent:  'lwd:open-aura',
};

// ── THE LWD EDIT SHOWCASE ───────────────────────────────────────────────────────
// Pulled from `magazine_posts` (published=true) at runtime. If empty, the
// section is skipped (truthful).
export const EDIT_SHOWCASE_COPY = {
  eyebrow:  'Editorial · The LWD Edit',
  // Kicker line, sets the transformation before the title. Frames the
  // section as elevation, not exposure.
  kicker:   'From venue to destination story',
  title:    'Where your work becomes editorial',
  body:     'Our quarterly publication features the venues, vendors and planners we believe in. Framed as destination stories, not listings. Read by high-net-worth couples planning across Europe, the US and beyond.',
  cta:      'Explore The LWD Edit',
  ctaHref:  '/the-edit',
};

// ── PUBLICATIONS (printed magazine issues) ─────────────────────────────────
// Pulled live from `magazine_issues` (status='published') at runtime. The
// section displays 3-4 most recent issues as magazine covers, with a CTA
// out to /publications. If the live query returns nothing, the section
// hides itself entirely.
export const PUBLICATIONS_COPY = {
  eyebrow:  'The Magazines',
  title:    'Quarterly publications, by invitation',
  body:     'Members are considered for inclusion in our printed and digital magazine issues. Each issue is curated, not sold.',
  // Standalone scarcity beat, pulled out of the body so it reads as a
  // discrete principle rather than a clause. Renders below the body in
  // gold caps, sets the "I want to be in this" tension before the covers.
  scarcity: 'A limited number of venues are selected for each issue.',
  cta:      'Browse Issues',
  ctaHref:  '/publications',
  // Maximum issues shown on the page. The query fetches the most recent.
  maxIssues: 4,
};

// ── THE STANDARD (selection criteria) ──────────────────────────────────────
// Five concise points. Phrased as discipline, not poetry.
export const SELECTION_CRITERIA = [
  'A proven track record in destination or high-end weddings.',
  'Consistent visual and service excellence, supported by editorial-quality photography.',
  'Strong international appeal and brand presence.',
  'Alignment with our editorial standards and brand voice.',
  'Capacity to deliver the guest experience that justifies the price.',
];

// ── REACH & AUDIENCE ───────────────────────────────────────────────────────
// Strict honesty rule applies here. If you don't have verified data, leave
// `verified` false and the public section will not render those numbers.
// Replace these placeholder values once analytics is wired up.
export const REACH_PANEL = {
  verified: false,                                  // ← set to true ONLY when each metric below is verified
  metrics: [
    { _isPlaceholder: true, label: 'Couples per month',   value: null, note: 'Pending verified analytics' },
    { _isPlaceholder: true, label: 'Curated listings',    value: null, note: 'Pending verified analytics' },
    { _isPlaceholder: true, label: 'Countries covered',   value: null, note: 'Pending verified analytics' },
    { _isPlaceholder: true, label: 'Avg. enquiries / listing / month', value: null, note: 'Pending verified analytics' },
  ],
};

// ── VOICES FROM THE COLLECTION (testimonials) ──────────────────────────────
// HARD RULE: do not list any testimonial here unless it is from a real
// member with explicit permission. If empty, the section is hidden.
// Schema for when you add real ones:
//   { quote: "...", attribution: "Venue or Planner Name", location: "City, Country" }
export const TESTIMONIALS = [
  // Empty until real member quotes are collected with permission.
];

// ── THE PROCESS ─────────────────────────────────────────────────────────────
export const PROCESS_STEPS = [
  { step: '01', title: 'Apply',         body: 'Submit your application below. Our editorial team reviews each submission personally.' },
  { step: '02', title: 'Curation',      body: 'Selected applicants are invited for a conversation. We discuss fit, positioning, and what membership would look like for your business.' },
  { step: '03', title: 'Onboarding',    body: 'Approved members are onboarded into the directory and considered for editorial inclusion in the next issue of The LWD Edit.' },
];

// ── INVESTMENT (pricing anchor) ────────────────────────────────────────────
// One sentence. Approved verbatim by the brief.
export const INVESTMENT_COPY = 'Membership is tailored based on visibility and editorial inclusion, typically starting from a four-figure annual investment.';

// ── SUCCESS PAGE ───────────────────────────────────────────────────────────
export const SUCCESS_CONTENT = {
  headline: 'Application Received',
  subline:  'Our team will review your submission within 24 hours.',
  stepsTitle: 'What happens next',
  steps: [
    'Review and internal curation.',
    'Shortlisted venues invited for conversation.',
    'Final selection and onboarding.',
  ],
  ctaLabel: 'While you wait, explore The LWD Edit',
  ctaHref:  '/the-edit',
};

// ── FORM (copy + field labels) ─────────────────────────────────────────────
// Field labels are deliberately lifted ("Property or Brand Name", not
// "Business Name") so the form reads as a curated submission, not a
// generic SaaS lead capture.
export const FORM = {
  preFrame:        'A brief introduction is all we require to begin the conversation.',
  title:           'Begin your application',
  description:     'Tell us about your venue or service. Our editorial team reviews every application personally.',
  cta:             'Submit for Consideration',
  investmentLabel: 'I\'m ready to move forward',
  investmentSubtext: 'Good luck - we\'re rooting for you.',
  fieldLabels: {
    category:        'Category',
    businessName:    'Property or Brand Name',
    name:            'Full Name',
    email:           'Email',
    phone:           'Phone',
    country:         'Country',
    region:          'Region or City',
    website:         'Website',
    instagram:       'Instagram',
    message:         'Tell us about your venue or service',
    messagePlaceholder: 'A few sentences. Why you, why now, what makes the work distinctive.',
  },
};
